/** Helpers aligned with backend CostAnalysisService::recalculateTotals */

export type InsuranceMode = 'global' | 'sections';

export interface AnalysisSectionLike {
  base_cost?: string | number | null;
  insurance_percentage?: string | number | null;
  calculated_cost?: string | number | null;
}

export interface AnalysisInsuranceLike {
  insurance_mode?: string | null;
  insurance_percentage?: string | number | null;
}

export const parseAmount = (value: string | number | null | undefined): number => {
  if (value == null || value === '') return 0;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(num) ? num : 0;
};

export const analysisInsuranceMode = (analysis?: AnalysisInsuranceLike | null): InsuranceMode =>
  analysis?.insurance_mode === 'global' ? 'global' : 'sections';

/** Effective insurance % for a section: global settings rate, or the section's own rate. */
export const sectionInsuranceRate = (
  section: AnalysisSectionLike,
  analysis?: AnalysisInsuranceLike | null
): number => {
  if (analysisInsuranceMode(analysis) === 'global') {
    return parseAmount(analysis?.insurance_percentage);
  }
  return parseAmount(section.insurance_percentage);
};

/** Insurance is a share of the section's FINAL amount (after profit and tax). */
export const sectionInsuranceAmount = (
  section: AnalysisSectionLike,
  analysis?: AnalysisInsuranceLike | null
): number => {
  const pct = sectionInsuranceRate(section, analysis);
  const basis = parseAmount(section.calculated_cost);
  return (basis * pct) / 100;
};

/** Sum of section insurance amounts */
export const totalSectionInsurance = (
  sections: AnalysisSectionLike[] | undefined,
  analysis?: AnalysisInsuranceLike | null
): number => (sections ?? []).reduce((sum, section) => sum + sectionInsuranceAmount(section, analysis), 0);

/** Effective insurance % of the grand total (for display) */
export const effectiveInsurancePctOnTotal = (totalCost: number, totalInsurance: number): number =>
  totalCost > 0 ? (totalInsurance / totalCost) * 100 : 0;

/** @deprecated Use effectiveInsurancePctOnTotal — insurance is now on the final amount. */
export const effectiveInsurancePctOnBase = (baseCost: number, totalInsurance: number): number =>
  baseCost > 0 ? (totalInsurance / baseCost) * 100 : 0;

/** Verify base + tax + insurance + profit ≈ total */
export const verifyTotalsSum = (
  base: number,
  tax: number,
  insurance: number,
  profit: number,
  total: number,
  tolerance = 1
): boolean => Math.abs(base + tax + insurance + profit - total) <= tolerance;

export const profitTypeLabel = (profitType: string | undefined): string => {
  switch (profitType) {
    case 'percentage_net':
      return 'درصد از کل';
    case 'amount':
      return 'مبلغ ثابت';
    default:
      return 'درصد از پایه';
  }
};
